import { LAUNCHER_LAYOUTS } from '@accounts/shared';
import type { LauncherLayout, SessionUser } from '@accounts/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { STR } from '../i18n';
import { ApiError, changeOwnPassword, updateMyPreferences } from '../lib/api';
import { initials } from '../lib/format';
import { queryKeys } from '../lib/queryKeys';
import { panelEntrySurface, surfaceById } from '../lib/surfaces';
import { showToast } from '../lib/toast';
import { useLogout, useSignOutEverywhere } from '../lib/useLogout';
import { AppearanceControl } from '../ui/Appearance';
import { Modal, ModalForm, ModalParagraph, ModalProse } from '../ui/Modal';
import { Callout } from '../ui/Primitives';
import { fieldLabelStyle, hintStyle, modalButtonStyle, textFieldStyle } from '../ui/styles';

/**
 * The launcher's account control — the demo's avatar button and its popover.
 *
 * Everything the signed-in person can do to their own account lives here: who they are, the
 * way back into the admin panel if they have one, their password, the two exits, and the
 * appearance controls. Appearance sits INSIDE this menu on this surface, so the sign-in
 * page's fixed corner is deliberately absent from the launcher — the control is the same one
 * either way, and two of it on one page is a defect, not a convenience.
 */

const MIN_NEW_PASSWORD_LENGTH = 12;

type Dialog = 'changePassword' | 'signOutEverywhere';

/** The selector's labels, keyed by the contract's own values so neither can drift. */
const LAYOUT_LABEL: Record<LauncherLayout, string> = {
  tiles: STR.launcher.account.layoutTiles,
  list: STR.launcher.account.layoutList,
};

export function AccountMenu({ user, layout }: { user: SessionUser; layout: LauncherLayout }) {
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState<Dialog | null>(null);

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  /** Set when a dialog was opened FROM the menu, so closing it returns focus here. */
  const returnFocus = useRef(false);

  const queryClient = useQueryClient();
  const logoutMutation = useLogout();
  const signOutEverywhereMutation = useSignOutEverywhere();

  /**
   * THE LAYOUT PREFERENCE — per-user and SERVER-SIDE, so it follows the person to any
   * browser. It rides the launcher applications read, so that one query is exactly what a
   * successful write invalidates; nothing else on the page depends on it.
   *
   * Deliberately unlike palette and mode, which stay in localStorage per device (the
   * sign-in page renders for visitors with no user row, so they have no server-side home).
   * Those two controls are untouched by this wave.
   */
  const layoutMutation = useMutation({
    mutationFn: (next: LauncherLayout) => updateMyPreferences({ launcherLayout: next }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.myApplications });
      showToast(STR.toast.layoutSaved(LAYOUT_LABEL[result.launcherLayout]));
    },
    onError: (error) => {
      showToast(error instanceof ApiError ? error.message : STR.errors.generic, 'warn');
    },
  });

  /*
   * THE ONE FLAGGED ADDITION BEYOND THE FROZEN DEMO.
   *
   * The demo has no route table, so it could not model this entry — and since EVERYONE lands
   * on the launcher after signing in, administrators included, this is the declared way into
   * the panel rather than a convenience. It is DERIVED, not an ad-hoc `user.isAdmin` check:
   * `panelEntrySurface` finds the surface marked `destination: 'panel-entry'` and returns it
   * only when `canEnter` passes against that surface's OWN declared gate, so this renders IFF
   * the actor may actually enter it and it points at whatever path the table declares — never
   * a hardcoded '/admin/users'. That satisfies the three-layer law (a nav entry renders iff
   * the actor may enter the surface). A deliberate, recorded divergence from a frozen demo,
   * in the same spirit as D23.
   */
  const adminPanel = panelEntrySurface(user);

  // Opening moves focus into the menu, exactly as the demo does.
  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLElement>('.menu-it')?.focus({ preventScroll: true });
  }, [open]);

  /*
   * Document-level dismissal, torn down whenever the menu is not open — a listener that
   * outlives the popover it serves is the leak this cleanup exists to prevent.
   *
   * The outside test is containment rather than the demo's `stopPropagation`: the click that
   * OPENS the menu is still travelling to the document when this listener is attached, and
   * that click's target is the button, which is inside the wrapper — so it cannot close the
   * menu it just opened.
   */
  useEffect(() => {
    if (!open) return;

    function onDocumentClick(event: MouseEvent) {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (wrapRef.current !== null && !wrapRef.current.contains(target)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      setOpen(false);
      buttonRef.current?.focus({ preventScroll: true });
    }

    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('click', onDocumentClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  /*
   * Focus returns to the button when a dialog closes. It runs in an effect rather than in the
   * close handler on purpose: `Modal` restores focus to whatever was active when it opened
   * during its own unmount cleanup, and React flushes that cleanup BEFORE this effect — so
   * doing it here is what makes the button the last word.
   */
  useEffect(() => {
    if (dialog !== null || !returnFocus.current) return;
    returnFocus.current = false;
    buttonRef.current?.focus({ preventScroll: true });
  }, [dialog]);

  function openDialog(kind: Dialog) {
    setOpen(false);
    returnFocus.current = true;
    setDialog(kind);
  }

  return (
    <div className="acct-wrap" ref={wrapRef}>
      <button
        type="button"
        className="acct"
        ref={buttonRef}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={STR.launcher.account.menuLabel}
        onClick={() => {
          setOpen((previous) => !previous);
        }}
      >
        <span className="avatar" aria-hidden="true">
          {initials(user.name)}
        </span>
        <span className="an">{user.name}</span>
      </button>

      {open ? (
        <div className="menu" ref={menuRef}>
          <div className="menu-id">
            <div className="n">{user.name}</div>
            <div className="e">{user.email}</div>
          </div>
          <div className="menu-sep" />

          {adminPanel === null ? null : (
            <Link
              className="menu-it"
              to={adminPanel.path}
              onClick={() => {
                setOpen(false);
              }}
            >
              {STR.launcher.account.adminPanel}
            </Link>
          )}
          {/* The way to the Security surface (2FA wave) — the design of record's account
              screen names the launcher's account menu as how it is reached. A plain surface
              link: the path comes from the one route declaration, never a literal. */}
          <Link
            className="menu-it"
            to={surfaceById('security').path}
            onClick={() => {
              setOpen(false);
            }}
          >
            {STR.launcher.account.security}
          </Link>
          <button
            type="button"
            className="menu-it"
            onClick={() => {
              openDialog('changePassword');
            }}
          >
            {STR.launcher.account.changePassword}
          </button>
          <button
            type="button"
            className="menu-it"
            disabled={logoutMutation.isPending}
            onClick={() => {
              logoutMutation.mutate();
            }}
          >
            {logoutMutation.isPending ? STR.common.loggingOut : STR.launcher.account.signOut}
          </button>
          <button
            type="button"
            className="menu-it danger"
            onClick={() => {
              openDialog('signOutEverywhere');
            }}
          >
            {STR.launcher.account.signOutEverywhere}
          </button>

          <div className="menu-sep" />
          {/* Furniture only — a labelled section, because on a surface where somebody is
              signed in appearance is a real account setting rather than a floating corner
              widget. The control inside it is the admin rail's, unchanged. */}
          <div className="menu-sec">
            <span className="mlab">{STR.launcher.account.appearance}</span>
            {/*
             * Side by side, and FILLING the row — only the admin rail (96px, genuinely
             * narrow) keeps the stacked, fixed-width layout. `.menu-sec` itself stays a
             * column so the label sits above this row rather than beside it.
             *
             * Two equal grid columns, `fill` on the control so neither button carries its
             * own width to fight the stretch. The `-10px` margin cancels `.menu-sec`'s own
             * horizontal padding (10px) so the pair's outer edge lands exactly where
             * Admin panel / Change password / Sign out already do, rather than sitting
             * 10px further in.
             */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 6,
                margin: '0 -10px',
              }}
            >
              <AppearanceControl fill />
            </div>

            {/*
              THE LAYOUT ROW — the one addition to this menu section, and it sits BELOW the
              two appearance buttons, which are unchanged in behaviour, storage, wording and
              treatment.

              It is a SELECTOR, not a cycle button: both options are always visible and the
              one in use is accent-filled and check-marked (`.lay`), so it can never be
              misread as "click to switch to the label you see". The helper line says the
              same thing in words.

              U3, A NAMED REVISION TO THE FROZEN DEMO (operator-ruled): this row now uses
              THE SAME CONTAINER as the appearance pair above — the identical two-equal-
              column grid, the identical 6px gap, the identical -10px bleed — because that
              is what makes the two rows the same total width with their buttons at the same
              x positions. The `.tgl` segmented shell it used to wear is gone: its padding
              and border were the whole height difference, and its inset was why the two
              rows' buttons never lined up. Nothing else about the selector changes — the
              filled, check-marked selected state is exactly as it was (`.lay` in
              components.css, which carries the matching 29px height).
            */}
            <span className="mlab" style={{ padding: '6px 2px 0' }}>
              {STR.launcher.account.layout}
            </span>
            <div
              role="group"
              aria-label={STR.launcher.account.layoutGroup}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 6,
                margin: '0 -10px',
              }}
            >
              {LAUNCHER_LAYOUTS.map((option) => (
                <button
                  key={option}
                  type="button"
                  className="seg lay"
                  aria-pressed={layout === option}
                  disabled={layoutMutation.isPending}
                  onClick={() => {
                    if (layout === option) return;
                    layoutMutation.mutate(option);
                  }}
                >
                  {LAYOUT_LABEL[option]}
                </button>
              ))}
            </div>
            <span
              className="tx-subtle"
              style={{ fontSize: 10.5, lineHeight: 1.45, padding: '0 2px' }}
            >
              {STR.launcher.account.layoutHint}
            </span>
          </div>
        </div>
      ) : null}

      {dialog === 'changePassword' ? (
        <ChangeOwnPasswordModal
          email={user.email}
          onClose={() => {
            setDialog(null);
          }}
        />
      ) : null}

      {dialog === 'signOutEverywhere' ? (
        <Modal
          title={STR.modals.signOutEverywhereSelf.title}
          onClose={() => {
            setDialog(null);
          }}
          footer={
            <>
              <button
                type="button"
                className="ghost"
                style={modalButtonStyle}
                onClick={() => {
                  setDialog(null);
                }}
              >
                {STR.common.cancel}
              </button>
              <button
                type="button"
                className="dbtn"
                style={modalButtonStyle}
                disabled={signOutEverywhereMutation.isPending}
                onClick={() => {
                  signOutEverywhereMutation.mutate();
                }}
              >
                {STR.modals.signOutEverywhereSelf.submit}
              </button>
            </>
          }
        >
          <ModalProse>
            <ModalParagraph>{STR.modals.signOutEverywhereSelf.body}</ModalParagraph>
          </ModalProse>
        </Modal>
      ) : null}
    </div>
  );
}

/**
 * Changing your own password while NOT walled. The wall has its own full screen; this is the
 * voluntary version, and the server re-issues this session's cookie with the bumped version
 * in the same response — so this device stays signed in and every other one is already dead.
 *
 * The length and match checks here are affordance; the server validates independently and is
 * the source of truth. The minimum is THIS APP's 12, reused from the wall's own hint rather
 * than restated, so the two can never drift apart.
 */
function ChangeOwnPasswordModal({ email, onClose }: { email: string; onClose: () => void }) {
  const queryClient = useQueryClient();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [invalidField, setInvalidField] = useState<'current' | 'new' | 'confirm' | null>(null);

  const mutation = useMutation({
    /*
     * No `redirect` is sent: there is no pending destination on the launcher, so the server
     * answers with `redirectTo: null` and this stays exactly where it is. The wall's screen
     * is the one place a pending destination exists, and it is the one place that sends it.
     */
    mutationFn: () => changeOwnPassword({ currentPassword, newPassword }),
    onSuccess: async () => {
      // The cookie was re-issued on this response; re-ask who we are so nothing renders from
      // a stale principal.
      await queryClient.invalidateQueries({ queryKey: queryKeys.me });
      onClose();
      showToast(STR.toast.passwordChanged);
    },
    onError: (error) => {
      setInvalidField(null);
      setFormError(error instanceof ApiError ? error.message : STR.errors.generic);
    },
  });

  const busy = mutation.isPending;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (currentPassword === '') {
      setInvalidField('current');
      setFormError(STR.modals.changeOwnPassword.currentRequired);
      return;
    }
    if (newPassword.length < MIN_NEW_PASSWORD_LENGTH) {
      setInvalidField('new');
      setFormError(STR.changePassword.tooShort);
      return;
    }
    if (newPassword !== confirmPassword) {
      setInvalidField('confirm');
      setFormError(STR.changePassword.mismatch);
      return;
    }

    setInvalidField(null);
    setFormError(null);
    mutation.mutate();
  }

  function clearError() {
    if (formError !== null) {
      setFormError(null);
      setInvalidField(null);
    }
  }

  function fieldClass(field: 'current' | 'new' | 'confirm'): string {
    return invalidField === field ? 'field field-over' : 'field';
  }

  return (
    <Modal
      title={STR.modals.changeOwnPassword.title}
      lead={STR.modals.changeOwnPassword.lead(email)}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ghost" style={modalButtonStyle} onClick={onClose}>
            {STR.common.cancel}
          </button>
          <button
            type="submit"
            form="change-own-password"
            className="prim"
            style={modalButtonStyle}
            disabled={busy}
          >
            {busy ? STR.changePassword.submitting : STR.modals.changeOwnPassword.submit}
          </button>
        </>
      }
    >
      <form id="change-own-password" onSubmit={handleSubmit} noValidate>
        <ModalForm>
          {formError === null ? null : (
            <Callout tone="danger" role="alert" style={{ fontWeight: 600 }}>
              {formError}
            </Callout>
          )}
          <label style={{ display: 'block' }}>
            <span className="tx-muted" style={fieldLabelStyle}>
              {STR.modals.changeOwnPassword.currentLabel}
            </span>
            <input
              className={fieldClass('current')}
              type="password"
              autoComplete="current-password"
              disabled={busy}
              style={textFieldStyle}
              value={currentPassword}
              onChange={(event) => {
                setCurrentPassword(event.target.value);
                clearError();
              }}
            />
          </label>
          <label style={{ display: 'block' }}>
            <span className="tx-muted" style={fieldLabelStyle}>
              {STR.modals.changeOwnPassword.newLabel}
            </span>
            <input
              className={fieldClass('new')}
              type="password"
              autoComplete="new-password"
              disabled={busy}
              style={textFieldStyle}
              value={newPassword}
              onChange={(event) => {
                setNewPassword(event.target.value);
                clearError();
              }}
            />
            <span className="tx-subtle" style={hintStyle}>
              {STR.changePassword.lengthHint}
            </span>
          </label>
          <label style={{ display: 'block' }}>
            <span className="tx-muted" style={fieldLabelStyle}>
              {STR.modals.changeOwnPassword.confirmLabel}
            </span>
            <input
              className={fieldClass('confirm')}
              type="password"
              autoComplete="new-password"
              disabled={busy}
              style={textFieldStyle}
              value={confirmPassword}
              onChange={(event) => {
                setConfirmPassword(event.target.value);
                clearError();
              }}
            />
          </label>
        </ModalForm>
      </form>
    </Modal>
  );
}
